import React, { useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { formatPrice } from 'utils/format';
import LeaderMenuItem from 'components/common/LeaderMenuItem';
import ExpandAction from 'components/common/ExpandAction';
import OrderSummaryItems from './OrderSummaryItems';

interface IProps {
  tickets: any[];
  addons: any[];
  ticketsCounts: Record<string, number>;
  addonsCounts: Record<string, number>;
  totalTicketsCount: number;
  totalAddonsCount: number;
  totalTicketsPrice: number;
  totalAddonsPrice: number;
  totalDonations: number;
  totalDiscount: number;
  totalPrice: number;
  taxes: number;
  currency: string;
  notExpandable?: boolean;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const useStyles = makeStyles()((theme) => ({
  h5: {
    ...theme.typography.h5,
    [theme.breakpoints.down('md')]: {
      ...theme.typography.h5,
    },
  },
}));

const formatTaxes = (taxes: number): string => taxes.toFixed(2);

const OrderSummary: React.FC<React.PropsWithChildren<IProps>> = ({
  tickets,
  addons,
  ticketsCounts,
  addonsCounts,
  totalTicketsCount,
  totalAddonsCount,
  totalTicketsPrice,
  totalAddonsPrice,
  totalDonations,
  totalDiscount,
  totalPrice,
  taxes,
  currency,
  notExpandable,
}) => {
  const { classes } = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [t] = useTranslation('public');

  return (
    <Grid container direction="column" spacing={3}>
      {!notExpandable && (
        <Grid item container spacing={1} alignItems="center">
          <Grid item xs>
            <Typography
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              className={classes.h5}
              variant="h5"
            >
              {t<string>('creatingOrder.orderSummary.title')}
            </Typography>
          </Grid>
          <Grid item>
            <ExpandAction edge="end" size="small" expanded={expanded} onExpandChange={setExpanded} />
          </Grid>
        </Grid>
      )}
      <Grid item container direction="column" spacing={2}>
        <Grid item>
          <OrderSummaryItems
            itemType="ticket"
            currency={currency}
            expanded={expanded}
            orderEntities={tickets}
            orderCounts={ticketsCounts}
            totalCount={totalTicketsCount}
            totalPrice={totalTicketsPrice}
          />
        </Grid>
        {totalAddonsCount > 0 && (
          <Grid item>
            <OrderSummaryItems
              itemType="addon"
              currency={currency}
              expanded={expanded}
              orderEntities={addons}
              orderCounts={addonsCounts}
              totalCount={totalAddonsCount}
              totalPrice={totalAddonsPrice}
            />
          </Grid>
        )}
        <Grid item>
          <LeaderMenuItem>
            <Typography variant="subtitle1">{t<string>('common:general.taxes')}</Typography>
            <Typography variant="subtitle1">{formatPrice(formatTaxes(taxes), currency, false, false)}</Typography>
          </LeaderMenuItem>
        </Grid>
        {totalDiscount > 0 && (
          <Grid item>
            <LeaderMenuItem>
              <Typography variant="subtitle1">{t<string>('common:general.promoCode')}</Typography>
              <Typography variant="subtitle1">{formatPrice(-totalDiscount, currency, false, true)}</Typography>
            </LeaderMenuItem>
          </Grid>
        )}
        {totalDonations > 0 && (
          <Grid item>
            <LeaderMenuItem>
              <Typography variant="subtitle1">{t<string>('common:general.donation')}</Typography>
              <Typography variant="subtitle1">{formatPrice(totalDonations, currency, false, false)}</Typography>
            </LeaderMenuItem>
          </Grid>
        )}
        <Grid item>
          <LeaderMenuItem>
            <Typography variant="h6">{t<string>('common:general.total')}</Typography>
            <Typography variant="h6">{formatPrice(totalPrice, currency, true, true)}</Typography>
          </LeaderMenuItem>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default OrderSummary;
